import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
};

/**
 * Input — follows the brand book "no 1px border" rule by sitting on a
 * surface-container-lowest pill with a subtle tonal shift instead of a border.
 * Focus state lifts the ring (orange ring is the global focus outline).
 *
 * Labels float above the field with uppercase-style tracking (brand §3).
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, hint, error, leftIcon, rightSlot, id, ...props }, ref) => {
    const inputId = id ?? `in-${Math.random().toString(36).slice(2, 9)}`;
    return (
      <label htmlFor={inputId} className="block">
        {label && (
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-label text-on-surface-variant">
            {label}
          </span>
        )}
        <span
          className={cn(
            // `app-field` — метка поля: оно ОСТАЁТСЯ белым и в песочном
            // кабинете, иначе непонятно, куда писать.
            'app-field flex items-center gap-2 rounded-button bg-surface-container-lowest px-4 h-12',
            'shadow-soft transition focus-within:shadow-ambient',
            error && 'ring-2 ring-error',
            className,
          )}
        >
          {leftIcon && <span className="text-on-surface-variant">{leftIcon}</span>}
          <input
            id={inputId}
            ref={ref}
            className="w-full bg-transparent text-on-surface placeholder:text-on-surface-variant/60 focus:outline-none"
            {...props}
          />
          {rightSlot}
        </span>
        {(error || hint) && (
          <span
            className={cn(
              'mt-1.5 block text-xs',
              error ? 'text-error' : 'text-on-surface-variant',
            )}
          >
            {error || hint}
          </span>
        )}
      </label>
    );
  },
);

Input.displayName = 'Input';
